/**
 * The dashboard's System tab: a handful of live numbers from the performance monitor, using the
 * same hooks (and therefore the same query cache) as the /performance page, so opening that page
 * from here shows the same reading rather than a second fetch that disagrees.
 *
 * The window is the last hour: a dashboard glance is "is it healthy now", and the Performance
 * page is one tap away for any other window.
 */

import { Link as RouterLink } from 'react-router-dom'
import { Alert, Box, Button, Chip, Grid, Paper, Stack, Typography } from '@mui/material'
import { Speed } from '@mui/icons-material'
import {
  usePerfDatabase,
  usePerfOverview,
  usePerfProbes,
} from '@features/performance/hooks/usePerformance'
import { StatTile } from '@features/performance/components/StatTile'
import {
  PROBE_OUTCOME_COLOR,
  PROBE_OUTCOME_LABEL,
  errorRateSeverity,
  formatMs,
  formatPercent,
  latencySeverity,
} from '@features/performance/components/format'

const WINDOW_HOURS = 1

/** Above this share of max_connections the shared Postgres pool is close to refusing services. */
const CONNECTION_WARN_PERCENT = 70

export function SystemHealthPanel() {
  const overview = usePerfOverview(WINDOW_HOURS)
  // top=1: the dashboard shows none of the statement lists, only the connection and cache readings.
  const database = usePerfDatabase(true, 1)
  const probes = usePerfProbes(24)

  const o = overview.data
  const db = database.data
  const connectionPercent = db && db.maxConnections > 0 ? (db.connections / db.maxConnections) * 100 : null
  const enabledProbes = (probes.data ?? []).filter((p) => p.enabled)

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1, minWidth: 0 }}>
          Last hour, both APIs
        </Typography>
        <Button component={RouterLink} to="/performance" startIcon={<Speed />} size="small" variant="outlined">
          Open Performance
        </Button>
      </Box>

      {overview.error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => overview.refetch()}>
              Retry
            </Button>
          }
        >
          Could not load request metrics.
        </Alert>
      )}
      {!overview.isLoading && o && o.totalRequests === 0 && (
        <Alert severity="info">Nothing recorded in the last hour yet. Both APIs write their totals once a minute.</Alert>
      )}

      <Grid container spacing={{ xs: 1, sm: 2 }}>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Requests / min"
            value={o ? o.requestsPerMinute.toFixed(1) : '—'}
            detail={o ? `${o.totalRequests.toLocaleString()} this hour` : undefined}
            loading={overview.isLoading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="p95 latency"
            value={formatMs(o?.p95Ms)}
            detail={o ? `p50 ${formatMs(o.p50Ms)}` : undefined}
            severity={o ? latencySeverity(o.p95Ms) : undefined}
            loading={overview.isLoading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Error rate"
            value={formatPercent(o?.errorRatePercent)}
            detail={o ? `${formatPercent(o.clientErrorRatePercent)} client (4xx)` : undefined}
            severity={o ? errorRateSeverity(o.errorRatePercent) : undefined}
            help="Server errors (5xx) as a share of all requests. Client errors are shown underneath and are usually not the API's fault."
            loading={overview.isLoading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Time in DB"
            value={formatPercent(o?.dbTimeSharePercent)}
            detail={o ? `${o.avgQueriesPerRequest.toFixed(1)} queries / request` : undefined}
            loading={overview.isLoading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="DB connections"
            value={db ? `${db.connections} / ${db.maxConnections}` : database.error ? 'Error' : '—'}
            detail={db ? `${db.activeConnections} active, ${db.idleInTransaction} idle in txn` : undefined}
            severity={
              connectionPercent == null ? undefined : connectionPercent >= CONNECTION_WARN_PERCENT ? 'warning' : 'success'
            }
            help="Every service shares one Postgres connection limit. Close to the maximum, new requests start failing to get a connection."
            loading={database.isLoading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="DB cache hit"
            value={formatPercent(db?.cacheHitRatioPercent)}
            detail={db ? `${db.deadlocks} deadlocks, uptime ${db.uptime ?? '—'}` : undefined}
            severity={db ? (db.cacheHitRatioPercent >= 99 ? 'success' : 'warning') : undefined}
            loading={database.isLoading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Slowest endpoint"
            value={o?.slowestEndpoints[0] ? formatMs(o.slowestEndpoints[0].p95Ms) : '—'}
            detail={
              o?.slowestEndpoints[0] ? (
                <Box component="span" sx={{ overflowWrap: 'anywhere' }}>
                  {o.slowestEndpoints[0].method} {o.slowestEndpoints[0].route}
                </Box>
              ) : undefined
            }
            loading={overview.isLoading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Uptime probes"
            value={
              o ? (o.failingProbes + o.degradedProbes === 0 ? 'All up' : `${o.failingProbes} down`) : '—'
            }
            detail={o ? `${o.degradedProbes} degraded` : undefined}
            severity={o ? (o.failingProbes > 0 ? 'error' : o.degradedProbes > 0 ? 'warning' : 'success') : undefined}
            loading={overview.isLoading}
          />
        </Grid>
      </Grid>

      <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
            Probes (24h uptime)
          </Typography>
          <Button component={RouterLink} to="/performance?tab=uptime" size="small">
            Manage
          </Button>
        </Box>
        {probes.isLoading ? (
          <Typography variant="body2" color="text.secondary">
            Loading probes...
          </Typography>
        ) : probes.error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => probes.refetch()}>
                Retry
              </Button>
            }
          >
            Could not load probes.
          </Alert>
        ) : enabledProbes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No probes are set up.
          </Typography>
        ) : (
          <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
            {enabledProbes.map((probe) => (
              <Box key={probe.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {probe.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatPercent(probe.uptimePercent)} up · p95 {formatMs(probe.p95DurationMs)}
                  </Typography>
                </Box>
                {probe.lastOutcome != null ? (
                  <Chip
                    size="small"
                    label={PROBE_OUTCOME_LABEL[probe.lastOutcome]}
                    color={PROBE_OUTCOME_COLOR[probe.lastOutcome]}
                  />
                ) : (
                  <Chip size="small" label="Not run" />
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  )
}
